import {
  ADJUVANT_STEP_GROUP_KEY,
  BIOSTIMULANT_STEP_GROUP_KEY,
  OPERATION_STEP_GROUP_KEY,
  PLANTPROTECTION_STEP_GROUP_KEY,
  SEED_STEP_GROUP_KEY,
  SOILINPUT_STEP_GROUP_KEY,
} from 'constants/uiConstants';
import {
  ADJUVANT_COLLECTION,
  BIOSTIMULANT_COLLECTION,
  CHEMICAL_COLLECTION,
  OPERATION_COLLECTION,
  SEEDSPECIES_COLLECTION,
  SOILINPUT_COLLECTION,
} from 'constants/gcConstants';
import {
  M_ADJUVANT_QUERY_KEY,
  M_BIOSTIMULANT_QUERY_KEY,
  M_CHEMICAL_QUERY_KEY,
  M_CROPS_QUERY_KEY,
  M_OPERATION_QUERY_KEY,
  M_SEARCH_ADJUVANT_QUERY_KEY,
  M_SEARCH_BIOSTIMULANT_QUERY_KEY,
  M_SEARCH_CHEMICAL_QUERY_KEY,
  M_SEARCH_CROPS_QUERY_KEY,
  M_SEARCH_OPERATION_QUERY_KEY,
  M_SEARCH_SOILINPUT_QUERY_KEY,
  M_SOILINPUT_QUERY_KEY,
} from 'constants/queryKeys';
import type { MergedCrop } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';
import {
  IActiveConstituent,
  InputStepType,
  PoisonSchedule,
} from 'interfaces/IInputStep';
import type { MWritableCollectionKey } from 'services/MasterDbServices';

import {
  M_CONSTRAINT_QUERY_KEY,
  M_SEARCH_CONSTRAINT_QUERY_KEY,
  M_SEARCH_TIMING_QUERY_KEY,
  M_TIMING_QUERY_KEY,
} from 'constants/queryKeys';

import { pickSeedSpeciesProductionFields } from 'utils/catalogueMergeUtils';

import type { StepSelectorOption } from './stepConfig';

// Records a user can add from a step picker (OP-2498). Timing, target and
// variety are added through their own quick confirms.
export type AddProductKind =
  | 'chemical'
  | 'adjuvant'
  | 'soilInput'
  | 'biostimulant'
  | 'operation'
  | 'crop';

export const NUTRIENT_KEYS = [
  'nutrientN',
  'nutrientP',
  'nutrientK',
  'nutrientS',
  'nutrientCa',
  'nutrientMg',
  'nutrientNa',
  'nutrientB',
  'nutrientCu',
  'nutrientFe',
  'nutrientMn',
  'nutrientZn',
  'nutrientMo',
  'nutrientCl',
  'nutrientSi',
  'nutrientNi',
  'nutrientCo',
] as const;

export type NutrientKey = (typeof NUTRIENT_KEYS)[number];

/** Nutrient removal per tonne of product, held on the crop (not the variety). */
export const REMOVAL_KEYS = [
  'productN',
  'productP',
  'productK',
  'productS',
  'productCa',
  'productMg',
  'productNa',
  'productB',
  'productCu',
  'productZn',
  'productMn',
  'productFe',
  'productMo',
  'productCl',
  'productSi',
] as const;

export type RemovalKey = (typeof REMOVAL_KEYS)[number];

interface AddProductConfig {
  collection: MWritableCollectionKey;
  stepType: InputStepType;
  groupKey: string;
  mQueryKey: string;
  searchQueryKey: string;
  /** Field the unit is stored under on the M document. */
  unitField: 'rateUnit' | 'operationUnit';
  /** Field the price or cost is stored under on the M document. */
  costField: 'price' | 'cost';
  /** True when the form asks for a unit; false when it is fixed (operation $/ha, crop kg). */
  hasUnitChoice: boolean;
  hasRate: boolean;
  /** Sent on Create so an M-only record has no G product to inherit it from. */
  useWater?: boolean;
}

export const ADD_PRODUCT_CONFIG: Record<AddProductKind, AddProductConfig> = {
  chemical: {
    collection: CHEMICAL_COLLECTION,
    stepType: InputStepType.CropProtection,
    groupKey: PLANTPROTECTION_STEP_GROUP_KEY,
    mQueryKey: M_CHEMICAL_QUERY_KEY,
    searchQueryKey: M_SEARCH_CHEMICAL_QUERY_KEY,
    unitField: 'rateUnit',
    costField: 'price',
    hasUnitChoice: true,
    hasRate: true,
    useWater: false,
  },
  adjuvant: {
    collection: ADJUVANT_COLLECTION,
    stepType: InputStepType.Adjuvant,
    groupKey: ADJUVANT_STEP_GROUP_KEY,
    mQueryKey: M_ADJUVANT_QUERY_KEY,
    searchQueryKey: M_SEARCH_ADJUVANT_QUERY_KEY,
    unitField: 'rateUnit',
    costField: 'price',
    hasUnitChoice: true,
    hasRate: true,
    useWater: true,
  },
  soilInput: {
    collection: SOILINPUT_COLLECTION,
    stepType: InputStepType.SoilInput,
    groupKey: SOILINPUT_STEP_GROUP_KEY,
    mQueryKey: M_SOILINPUT_QUERY_KEY,
    searchQueryKey: M_SEARCH_SOILINPUT_QUERY_KEY,
    unitField: 'rateUnit',
    costField: 'price',
    hasUnitChoice: true,
    hasRate: true,
  },
  biostimulant: {
    collection: BIOSTIMULANT_COLLECTION,
    stepType: InputStepType.Biostimulant,
    groupKey: BIOSTIMULANT_STEP_GROUP_KEY,
    mQueryKey: M_BIOSTIMULANT_QUERY_KEY,
    searchQueryKey: M_SEARCH_BIOSTIMULANT_QUERY_KEY,
    unitField: 'rateUnit',
    costField: 'price',
    hasUnitChoice: true,
    hasRate: true,
  },
  operation: {
    collection: OPERATION_COLLECTION,
    stepType: InputStepType.Application,
    groupKey: OPERATION_STEP_GROUP_KEY,
    mQueryKey: M_OPERATION_QUERY_KEY,
    searchQueryKey: M_SEARCH_OPERATION_QUERY_KEY,
    unitField: 'operationUnit',
    costField: 'cost',
    hasUnitChoice: false,
    hasRate: false,
  },
  crop: {
    collection: SEEDSPECIES_COLLECTION,
    stepType: InputStepType.Seed,
    groupKey: SEED_STEP_GROUP_KEY,
    mQueryKey: M_CROPS_QUERY_KEY,
    searchQueryKey: M_SEARCH_CROPS_QUERY_KEY,
    unitField: 'rateUnit',
    costField: 'cost',
    hasUnitChoice: false,
    hasRate: true,
  },
};

/** The picker tab (step group) that offers adding each kind of record. */
export const getAddProductKind = (groupKey: string): AddProductKind | null =>
  (Object.keys(ADD_PRODUCT_CONFIG) as AddProductKind[]).find(
    (kind) => ADD_PRODUCT_CONFIG[kind].groupKey === groupKey
  ) ?? null;

/** An active constituent being entered in the form; `key` only identifies the row. */
export interface ActiveConstituentDraft {
  key: string;
  name: string;
  concentration?: number | null;
  concentrationUnit?: IUnit | null;
  moaGroup?: { id: string; name: string } | null;
}

export interface AddRecordValues {
  name: string;
  rate?: number | null;
  /** The unit the user chose, or the fixed default for operations and crops. */
  unit?: IUnit | null;
  /** Price per unit, or cost for operations and crops. */
  cost?: number | null;
  // Plant protection (chemical, adjuvant)
  formulationCode?: string | null;
  snapManufacturer?: string | null;
  apvmaNumber?: string | null;
  poisonSchedule?: PoisonSchedule | null;
  activeUnit?: IUnit | null;
  activeConstituents?: ActiveConstituentDraft[];
  // Soil input
  isLiquid?: boolean;
  nutrients?: Partial<Record<NutrientKey, number | null>>;
  // Crop
  removal?: Partial<Record<RemovalKey, number | null>>;
}

const isFilledNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const trimmedOrNull = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const hasConstituentData = (draft: ActiveConstituentDraft): boolean =>
  !!draft.name.trim() ||
  isFilledNumber(draft.concentration) ||
  !!draft.concentrationUnit?.unit ||
  !!draft.moaGroup;

/** A constituent row left completely blank is ignored; one with details needs a name. */
const getFilledConstituents = (
  values: AddRecordValues
): ActiveConstituentDraft[] =>
  (values.activeConstituents ?? []).filter(hasConstituentData);

const toActiveConstituent = (
  draft: ActiveConstituentDraft
): IActiveConstituent => ({
  name: draft.name.trim(),
  concentration: isFilledNumber(draft.concentration)
    ? draft.concentration
    : null,
  concentrationUnit: draft.concentrationUnit?.unit
    ? draft.concentrationUnit
    : null,
  moaGroupId: draft.moaGroup?.id ?? null,
  snapMoaGroup: draft.moaGroup?.name ?? null,
});

/** The user can save once the name is set, a unit is chosen where asked, and numbers are not negative. */
export const isAddRecordValid = (
  kind: AddProductKind,
  values: AddRecordValues
): boolean => {
  const config = ADD_PRODUCT_CONFIG[kind];
  if (!values.name.trim()) return false;
  if (config.hasUnitChoice && !values.unit?.unit) return false;
  const constituents = getFilledConstituents(values);
  if (constituents.some((draft) => !draft.name.trim())) return false;
  const numbers = [
    values.rate,
    values.cost,
    ...Object.values(values.nutrients ?? {}),
    ...Object.values(values.removal ?? {}),
    ...constituents.map((draft) => draft.concentration),
  ];
  return numbers.every((n) => n == null || (isFilledNumber(n) && n >= 0));
};

/**
 * The type-specific fields sent on Create. Only filled values are sent, and
 * never null, because null on an M document hides the G value.
 */
export const buildAddRecordFields = (
  kind: AddProductKind,
  values: AddRecordValues
): Record<string, unknown> => {
  const config = ADD_PRODUCT_CONFIG[kind];
  const fields: Record<string, unknown> = { name: values.name.trim() };

  if (config.hasRate && isFilledNumber(values.rate)) fields.rate = values.rate;
  if (isFilledNumber(values.cost)) fields[config.costField] = values.cost;
  if (values.unit?.unit) fields[config.unitField] = values.unit;
  if (config.useWater !== undefined) fields.useWater = config.useWater;

  if (kind === 'chemical' || kind === 'adjuvant') {
    const formulationCode = trimmedOrNull(values.formulationCode);
    if (formulationCode) fields.formulationCode = formulationCode;
    const manufacturer = trimmedOrNull(values.snapManufacturer);
    if (manufacturer) {
      fields.snapManufacturer = manufacturer;
      fields.manufacturerId = null;
    }
    const apvmaNumber = trimmedOrNull(values.apvmaNumber);
    if (apvmaNumber) fields.apvmaNumber = apvmaNumber;
    if (values.poisonSchedule != null) {
      fields.poisonSchedule = values.poisonSchedule;
    }
    if (values.activeUnit?.unit) fields.activeUnit = values.activeUnit;
    // A list replaces the stored one, so an empty list is never sent.
    const constituents = getFilledConstituents(values);
    if (constituents.length > 0) {
      fields.activeConstituents = constituents.map(toActiveConstituent);
    }
  }

  if (kind === 'soilInput') {
    if (values.isLiquid !== undefined) fields.isSprays = values.isLiquid;
    NUTRIENT_KEYS.forEach((key) => {
      const value = values.nutrients?.[key];
      if (isFilledNumber(value)) fields[key] = value;
    });
  }

  if (kind === 'crop') {
    REMOVAL_KEYS.forEach((key) => {
      const value = values.removal?.[key];
      if (isFilledNumber(value)) fields[key] = value;
    });
  }

  return fields;
};

/**
 * The picker option for a record just added, built from the form values so it
 * can be selected straight away, without waiting for the lists to refetch.
 * Matches the shape the catalogue hooks produce.
 */
export const buildAddRecordOption = (
  kind: AddProductKind,
  globalId: string,
  values: AddRecordValues
): StepSelectorOption => {
  const config = ADD_PRODUCT_CONFIG[kind];
  const name = values.name.trim();
  const option: StepSelectorOption = {
    label: name,
    item: name,
    globalId,
    rate: config.hasRate ? (values.rate ?? null) : 1,
    cost: values.cost ?? null,
    unit: values.unit ?? null,
  };

  if (config.useWater !== undefined) option.useWater = config.useWater;

  if (kind === 'chemical' || kind === 'adjuvant') {
    option.formulationCode = trimmedOrNull(values.formulationCode);
    option.apvmaNumber = trimmedOrNull(values.apvmaNumber);
    option.poisonSchedule = values.poisonSchedule ?? null;
    option.snapManufacturer = trimmedOrNull(values.snapManufacturer);
    option.manufacturerId = null;
    option.activeConstituents = getFilledConstituents(values).map(
      toActiveConstituent
    );
  }

  // A new crop has no varieties yet; they are added from the variety list.
  if (kind === 'crop') option.varieties = [];

  if (kind === 'soilInput') {
    option.isLiquid = values.isLiquid ?? false;
    NUTRIENT_KEYS.forEach((key) => {
      option[key] = values.nutrients?.[key] ?? null;
    });
  }

  return option;
};

// Timing and target need only a name, so they are added by a quick confirm
// rather than a form (OP-2498).
export type QuickRecordKind = 'timing' | 'target';

export const QUICK_RECORD_CONFIG: Record<
  QuickRecordKind,
  { collection: MWritableCollectionKey; mQueryKey: string; searchQueryKey: string }
> = {
  timing: {
    collection: 'timing',
    mQueryKey: M_TIMING_QUERY_KEY,
    searchQueryKey: M_SEARCH_TIMING_QUERY_KEY,
  },
  target: {
    collection: 'constraint',
    mQueryKey: M_CONSTRAINT_QUERY_KEY,
    searchQueryKey: M_SEARCH_CONSTRAINT_QUERY_KEY,
  },
};

/**
 * A crop just added, in the shape the plan's crop field works with, so it can
 * be selected straight away (with its nutrient removal, which Nutrition reads).
 */
export const buildAddedMergedCrop = (
  globalId: string,
  values: AddRecordValues
): MergedCrop => ({
  globalId,
  name: values.name.trim(),
  varieties: [],
  cost: values.cost ?? null,
  rate: values.rate ?? null,
  rateUnit: values.unit ?? null,
  ...pickSeedSpeciesProductionFields(values.removal ?? null),
});
